import { type Address } from 'viem';
import { useUserStore } from '~/stores/user/useUserStore';
import { EvmItemType, type EvmOtherRecipientType } from '~/service/EVM/types';
import { useNuxtApp } from '#app';
import type { EvmMarketplace } from '~/service/EVM/config/types';
import { NULL_ADDRESS } from '~/service/EVM/consts';
import type { ContractSettings } from '~/service/api/types';

type ListingType = {
    paramNetwork: User['network'];
    itemType: AppWTFNft['itemType'];
    value: number;
    endTime: string;
    amount?: number;
    marketContract: ContractSettings;
    marketplace?: EvmMarketplace;
    identifier: string;
    contract: Address;
    otherRecipients: EvmOtherRecipientType[];
    uuid: AppWTFNft['uuid'];
    currencyItemType: EvmItemType;
    currencyTokenAddress: Address;
};

// /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
// function customStringify(value: any): string {
//     return JSON.stringify(value, (_, v) =>
//         typeof v === 'bigint' ? v.toString() : v
//     );
// }

export function useListing(initiatorKey?: string) {
    const { $provider } = useNuxtApp();
    const evmCore = useEVMCore();
    const { order: orderApi } = useEVMApi();

    const listingService = evmCore.createEvmListing($provider);

    const userStore = useUserStore();

    const createListingOrder = async ({
        paramNetwork,
        value,
        marketplace,
        contract,
        identifier,
        marketContract,
        itemType,
        endTime,
        amount,
        otherRecipients,
        currencyItemType,
        currencyTokenAddress
    }: ListingType) => {
        if (userStore.user && marketplace) {
            return await listingService.eventEmitter.emit(
                'fullListingProcess',
                async () => {
                    await listingService.ensureCorrectNetwork({ currentChainId: userStore.chainId, targetNetwork: paramNetwork });

                    const paramChain = listingService.getChainByNetwork(paramNetwork);

                    const { order, signature } = await listingService.buildOrder({
                        price: value,
                        paramChainId: paramChain.id,
                        userAddress: userStore.user as Address,
                        marketplace,
                        marketContract,
                        contract,
                        identifier,
                        itemType,
                        currencyItemType,
                        currencyTokenAddress,
                        otherRecipients,
                        endTime,
                        amount
                    });

                    const firstOffer = order.offer[0];

                    if (firstOffer) {
                        await orderApi.fillOrder(
                            {
                                user: userStore.user as Address,
                                chain: paramNetwork
                            },
                            {
                                parameters: {
                                    ...order,
                                    startTime: String(order.startTime),
                                    endTime: String(order.endTime),
                                    consideration: order.consideration.map(item => ({
                                        token: item.token as Address,
                                        recipient: item.recipient as Address,
                                        itemType: item.itemType,
                                        endAmount: String(item.endAmount),
                                        startAmount: String(item.startAmount),
                                        identifierOrCriteria: String(item.identifierOrCriteria)
                                    })),
                                    offer: [
                                        {
                                            token: firstOffer.token as Address,
                                            itemType: firstOffer.itemType,
                                            endAmount: String(firstOffer.endAmount),
                                            startAmount: String(firstOffer.startAmount),
                                            identifierOrCriteria: String(firstOffer.identifierOrCriteria)
                                        }
                                    ]
                                },
                                protocolAddress: NULL_ADDRESS,
                                signature
                            }
                        );
                    }

                    return { order, signature };
                },
                { isApprove: true },
                initiatorKey
            );
        }
        else {
            userStore.showUserIsDisconnect();
        }
    };
    return {
        createListingOrder
    };
}
